import { HttpErrorResponse } from '@angular/common/http';
import { ParticipantRegistrationMetric } from 'app/library/participant-registration-metric/models/participant-registration-metric.model';
import { ImportStatus } from 'app/library/scorm/models/import-status.model';
import { ICreateEntityFailureActionPayload } from 'app/projects/entity/src/lib/interfaces/create-entity-failure-payload.interface';
import { IFetchEntityFailureActionPayload } from 'app/projects/entity/src/lib/interfaces/fetch-entity-failure-payload.interface';
import { IUpdateEntityFailureActionPayload } from 'app/projects/entity/src/lib/interfaces/update-entity-failure-payload.interface';
import { NotifyType } from 'app/projects/shared/src/lib/enums/notify-type.enum';
import { ScheduledJob } from '../../scheduler/models/scheduled-job.model';
import { SurveyInfo } from '../../survey-section/models/survey-info.model';
import { ICreateClasstimeFeedbackActionPayload } from '../interfaces/create-classtime-feedback.action-payload';
import { ICreateClasstimeQuizActionPayload } from '../interfaces/create-classtime-quiz.action-payload';
import { FeedbackSection } from '../models/feedback-section.model';
import { QuizSection } from '../models/quiz-section.model';
import { Section } from '../models/section.model';
import { SurveySection } from '../models/survey-section.model';
import { SectionMetric } from '../models/section-metrics.model';
import { IUpdateClasstimeQuizActionPayload } from '../interfaces/update-classtime-quiz.action-payload';

export class SetSection {
    static readonly type = '[Section] SetSection';

    constructor(public payload: Section) {}
}

export class UnsetSection {
    static readonly type = '[Section] UnsetSection';
}

export class FetchSection {
    static readonly type = '[Section] FetchSection';

    constructor(public payload: string) {}
}

export class FetchSectionSuccess {
    static readonly type = '[Section] FetchSectionSuccess';

    constructor(public payload: Section) {}
}

export class FetchSectionFailure {
    static readonly type = '[Section] FetchSectionFailure';

    constructor(public payload: IFetchEntityFailureActionPayload) {}
}

export class FetchSectionById {
    static readonly type = '[Section] FetchSectionById';

    constructor(public payload: string) {}
}

export class FetchSectionByIdSuccess {
    static readonly type = '[Section] FetchSectionByIdSuccess';

    constructor(public payload: Section) {}
}

export class FetchSectionByIdFailure {
    static readonly type = '[Section] FetchSectionByIdFailure';
}

export class CreateSection {
    static readonly type = '[Section] CreateSection';

    constructor(public payload: Section) {}
}

export class CreateSectionSuccess {
    static readonly type = '[Section] CreateSectionSuccess';

    constructor(public payload: Section) {}
}

export class CreateSectionFailure {
    static readonly type = '[Section] CreateSectionFailure';

    constructor(public payload: ICreateEntityFailureActionPayload) {}
}

export class UpdateSection {
    static readonly type = '[Section] UpdateSection';

    constructor(public payload: Section) {}
}

export class UpdateSectionSuccess {
    static readonly type = '[Section] UpdateSectionSuccess';

    constructor(public payload: Section) {}
}

export class UpdateSectionFailure {
    static readonly type = '[Section] UpdateSectionFailure';

    constructor(public payload: IUpdateEntityFailureActionPayload) {}
}

export class DeleteSection {
    static readonly type = '[Section] DeleteSection';

    constructor(
        public payload: {
            id: string;
            notify: NotifyType;
        }
    ) {}
}

export class DeleteSectionSuccess {
    static readonly type = '[Section] DeleteSectionSuccess';

    constructor(public payload: string) {}
}

export class DeleteSectionFailure {
    static readonly type = '[Section] DeleteSectionFailure';

    constructor(public payload: string) {}
}

export class SetSections {
    static readonly type = '[Section] SetSections';

    constructor(public payload: Section[]) {}
}

export class ToggleSectionCompletion {
    static readonly type = '[Section] ToggleSectionCompletion';

    constructor(public payload: string) {}
}

export class ToggleSectionCompletionSuccess {
    static readonly type = '[Section] ToggleSectionCompletionSuccess';

    constructor(public payload: Section[]) {}
}

export class ToggleSectionCompletionFailure {
    static readonly type = '[Section] ToggleSectionCompletionFailure';

    constructor(public payload: HttpErrorResponse) {}
}

export class FetchModuleSections {
    static readonly type = '[Section] FetchModuleSections';

    constructor(public payload: string) {}
}

export class FetchModuleSectionsSuccess {
    static readonly type = '[Section] FetchModuleSectionsSuccess';

    constructor(public payload: Section[]) {}
}

export class FetchModuleSectionsFailure {
    static readonly type = '[Section] FetchModuleSectionsFailure';
}

export class UnpublishSection {
    static readonly type = '[Section] UnpublishSection';

    constructor(public payload: string) {}
}

export class UnpublishSectionSuccess {
    static readonly type = '[Section] UnpublishSectionSuccess';

    constructor() {}
}

export class UnpublishSectionFailure {
    static readonly type = '[Section] UnpublishSectionFailure';
}

export class CreateClasstimeQuiz {
    static readonly type = '[Section] CreateClasstimeQuiz';

    constructor(
        public payload: {
            sectionId: string;
            options: ICreateClasstimeQuizActionPayload;
        }
    ) {}
}

export class CreateClasstimeQuizSuccess {
    static readonly type = '[Section] CreateClasstimeQuizSuccess';

    constructor(
        public payload: {
            section: QuizSection;
        }
    ) {}
}

export class CreateClasstimeQuizFailure {
    static readonly type = '[Section] CreateClasstimeQuizFailure';
}

export class UpdateClasstimeQuiz {
    static readonly type = '[Section] UpdateClasstimeQuiz';

    constructor(
        public payload: {
            sectionId: string;
            options: IUpdateClasstimeQuizActionPayload;
        }
    ) {}
}

export class UpdateClasstimeQuizSuccess {
    static readonly type = '[Section] UpdateClasstimeQuizSuccess';

    constructor(
        public payload: {
            section: QuizSection;
        }
    ) {}
}

export class UpdateClasstimeQuizFailure {
    static readonly type = '[Section] UpdateClasstimeQuizFailure';
}

export class CreateClasstimeFeedback {
    static readonly type = '[Section] CreateClasstimeFeedback';

    constructor(
        public payload: {
            sectionId: string;
            options: ICreateClasstimeFeedbackActionPayload;
        }
    ) {}
}

export class CreateClasstimeFeedbackSuccess {
    static readonly type = '[Section] CreateClasstimeFeedbackSuccess';

    constructor(
        public payload: {
            section: FeedbackSection;
        }
    ) {}
}

export class CreateClasstimeFeedbackFailure {
    static readonly type = '[Section] CreateClasstimeFeedbackFailure';
}

export class SetSurveySection {
    static readonly type = '[Section] SetSurveySection';

    constructor(
        public payload: {
            sectionId: string;
            surveyInfo: SurveyInfo;
            descriptionVisibility: boolean;
            description?: string;
            templateId?: string;
        }
    ) {}
}

export class SetSurveyStatus {
    static readonly type = '[Section] SetSurveyStatus';

    constructor(
        public payload: {
            sectionId: string;
            status: string;
        }
    ) {}
}

export class SetSurveySectionExternal {
    static readonly type = '[Section] SetSurveySectionExternal';

    constructor(
        public payload: {
            sectionId: string;
            url: string;
            description?: string;
        }
    ) {}
}

export class SetSurveySectionSuccess {
    static readonly type = '[Section] SetSurveySectionSuccess';

    constructor(
        public payload: {
            section: SurveySection;
        }
    ) {}
}

export class SetSurveySectionFailure {
    static readonly type = '[Section] SetSurveySectionFailure';
}

export class SubmitSurveyResponses {
    static readonly type = '[Section] SubmitSurveyResponses';

    constructor(public payload: string) {}
}

export class SubmitSurveyResponsesSuccess {
    static readonly type = '[Section] SubmitSurveyResponsesSuccess';
}

export class SubmitSurveyResponsesFailure {
    static readonly type = '[Section] SubmitSurveyResponsesFailure';
}

export class RefreshSurveyDataReport {
    static readonly type = '[Section] RefreshSurveyDataReport';

    constructor(public payload: string) {}
}

export class RefreshSurveyDataReportSuccess {
    static readonly type = '[Section] RefreshSurveyDataReportSuccess';
}

export class RefreshSurveyDataReportFailure {
    static readonly type = '[Section] RefreshSurveyDataReportFailure';
}

export class RefreshQuizDataReport {
    static readonly type = '[Section] RefreshQuizDataReport';

    constructor(public payload: string) {}
}

export class RefreshQuizDataReportSuccess {
    static readonly type = '[Section] RefreshQuizDataReportSuccess';
}

export class RefreshQuizDataReportFailure {
    static readonly type = '[Section] RefreshQuizDataReportFailure';
}

export class RefreshFeedbackDataReport {
    static readonly type = '[Section] RefreshFeedbackDataReport';

    constructor(public payload: string) {}
}

export class RefreshFeedbackDataReportSuccess {
    static readonly type = '[Section] RefreshFeedbackDataReportSuccess';
}

export class RefreshFeedbackDataReportFailure {
    static readonly type = '[Section] v';
}
export class GetLaunchCourseUrl {
    static readonly type = '[Section] getLaunchCourseUrl';

    constructor(public payload: string) {}
}

export class GetLaunchCourseUrlSuccess {
    static readonly type = '[Section] GetLaunchCourseUrlSuccess';

    constructor(public payload: any) {}
}

export class GetLaunchCourseUrlFailure {
    static readonly type = '[Section] GetLaunchCourseUrlFailure';
}

export class GetParticipantScormRegistrationMetrics {
    static readonly type = '[Section] GetParticipantScormRegistrationMetrics';

    constructor(public payload: string) {}
}

export class GetParticipantScormRegistrationMetricsSuccess {
    static readonly type = '[Section] GetParticipantScormRegistrationMetricsSuccess';

    constructor(public payload: ParticipantRegistrationMetric) {}
}

export class GetParticipantScormRegistrationMetricsFailure {
    static readonly type = '[Section] GetParticipantScormRegistrationMetricsFailure';
}

export class ResetParticipantScormRegistrationMetrics {
    static readonly type = '[Section] ResetParticipantScormRegistrationMetrics';
}

export class GetSectionMetrics {
    static readonly type = '[Section] GetSectionMetrics';

    constructor(public payload: string) {}
}

export class GetSectionMetricsSuccess {
    static readonly type = '[Section] GetSectionMetricsSuccess';

    constructor(public payload: SectionMetric) {}
}

export class GetSectionMetricsFailure {
    static readonly type = '[Section] GetSectionMetricsFailure';
}

export class UnsetSectionMetrics {
    static readonly type = '[Section] UnsetSectionMetrics';
}

export class AddScheduleToSection {
    static readonly type = '[Section] AddScheduleToSection';

    constructor(
        public payload: {
            scheduledJob: ScheduledJob;
            entity: Section;
        }
    ) {}
}

export class UpdateScheduleInSection {
    static readonly type = '[Section] UpdateSchedulelInSection';

    constructor(
        public payload: {
            scheduledJob: ScheduledJob;
            entity: Section;
        }
    ) {}
}

export class RemoveScheduleFromSection {
    static readonly type = '[Section] RemoveScheduleFromSection';

    constructor(
        public payload: {
            scheduledJobId: string;
            entity: Section;
        }
    ) {}
}

export class SetSectionsMandatory {
    static readonly type = '[Section] SetSectionsMandatory';

    constructor(
        public payload: {
            sections: Section[];
            isMandatory: boolean;
        }
    ) {}
}

export class SetSectionsMandatorySuccess {
    static readonly type = '[Section] SetSectionsMandatorySuccess';

    constructor(public payload: Section[]) {}
}

export class SetSectionsMandatoryFailure {
    static readonly type = '[Section] SetSectionsMandatoryFailure';
}

export class FetchSectionCourse {
    static readonly type = '[Scorm] FetchSectionCourse';

    constructor(
        public payload: {
            courseId: string;
        }
    ) {}
}

export class FetchSectionCourseSuccess {
    static readonly type = '[Scorm] FetchSectionCourseSuccess';
}

export class FetchSectionCourseFailure {
    static readonly type = '[Scorm] FetchSectionCourseFailure';
}

export class FetchSectionImportStatus {
    static readonly type = '[Scorm] FetchSectionImportStatus';

    constructor(
        public payload: {
            jobId: string;
            entityId: string;
        }
    ) {}
}

export class FetchSectionImportStatusSuccess {
    static readonly type = '[Scorm] FetchSectionImportStatusSuccess';

    constructor(public payload: ImportStatus) {}
}

export class FetchSectionImportStatusFailure {
    static readonly type = '[Scorm] FetchSectionImportStatusFailure';
}
